/**
 * Passkey as a REAL second factor — Supabase-native WebAuthn MFA.
 *
 * ## Why this file exists next to lib/webauthn.ts, which already says passkeys
 *
 * The two are different things and both are wanted.
 *
 *   lib/webauthn.ts  — SELF-HOSTED WebAuthn against Elreign's own backend
 *                      (@simplewebauthn/server on Azure Functions, credentials
 *                      in Cosmos). It stamps `app_metadata.mfa_challenge_at`,
 *                      so it satisfies STEP-UP. It does NOT raise `aal`,
 *                      because Supabase never saw the ceremony.
 *
 *   THIS FILE        — Supabase's own `webauthn` FACTOR. Supabase performs the
 *                      ceremony, so a successful assertion issues a token with
 *                      `aal: 'aal2'` and `amr: [... 'mfa/webauthn']` exactly as
 *                      TOTP does. That is what makes a passkey a real
 *                      ALTERNATIVE to an authenticator app rather than an
 *                      extra convenience on top of one.
 *
 * lib/webauthn.ts carries a header saying Supabase "does NOT surface a WebAuthn
 * MFA/AAL2 factor through the SDK". That was true when it was written and is no
 * longer: `@supabase/auth-js` 2.105.3 — the version installed here — declares
 * `FactorTypes = ['totp', 'phone', 'webauthn']`, ships a `WebAuthnApi` on
 * `supabase.auth.mfa.webauthn`, and lists `'mfa/webauthn'` among its AMR
 * methods. Verified against the installed package's own type declarations, not
 * the docs. The self-hosted path is left exactly as it was — it is load-bearing
 * for step-up and for resolution-signing evidence.
 *
 * ## Two things that can make this unavailable, and neither may throw at a user
 *
 *   1. The BROWSER may have no WebAuthn (older browsers, jsdom under test).
 *   2. The GoTrue PROJECT may not have WebAuthn factors switched on. Supabase
 *      marks the whole `mfa.webauthn` API `@experimental`, and an unconfigured
 *      project answers enrolment with an error rather than a factor.
 *
 * Neither is a defect and neither is the user's fault, so both resolve to a
 * plain "not available here" that the surface renders as a muted note. The
 * authenticator app remains the always-present path — a passkey is never the
 * only way in, so nothing here can lock anybody out. That mirrors the honest-
 * surface rule the Security Centre already follows for the self-hosted passkey
 * card (never advertise a capability whose every submit would fail).
 */

import { supabase } from './supabase';
import { isWebAuthnSupported } from './webauthn';

/** Shape of the `webauthn` member we rely on. Narrowed by hand rather than
 *  imported, so that a supabase-js without it (or a test double that stubs
 *  `auth.mfa` with only the TOTP methods) is a clean `undefined` check instead
 *  of a TypeError at call time. */
interface SupabaseWebAuthnApi {
  register: (params: {
    friendlyName: string;
    webauthn?: { rpId?: string; rpOrigins?: string[]; signal?: AbortSignal };
  }) => Promise<{ data: unknown; error: Error | null }>;
  authenticate: (params: {
    factorId: string;
    webauthn?: { rpId?: string; rpOrigins?: string[]; signal?: AbortSignal };
  }) => Promise<{ data: unknown; error: Error | null }>;
}

function webauthnApi(): SupabaseWebAuthnApi | null {
  const mfa = supabase.auth.mfa as unknown as { webauthn?: Partial<SupabaseWebAuthnApi> };
  const api = mfa.webauthn;
  if (!api || typeof api.register !== 'function' || typeof api.authenticate !== 'function') {
    return null;
  }
  return api as SupabaseWebAuthnApi;
}

/** True when a Supabase-native passkey factor is even worth offering: the SDK
 *  exposes the API AND this browser can run a ceremony. Says nothing about
 *  whether the PROJECT has the factor enabled — only an enrolment attempt can
 *  answer that, and `enrollPasskeyFactor` reports it honestly. */
export function isPasskeyFactorSupported(): boolean {
  return webauthnApi() !== null && isWebAuthnSupported();
}

/** A verified passkey factor as the Security Centre and the verify screen see it. */
export interface PasskeyFactor {
  id: string;
  friendlyName: string | undefined;
  createdAt: string;
}

/**
 * Every VERIFIED Supabase `webauthn` factor on the account.
 *
 * Reads the union of `data.all` and `data.webauthn` for the same reason
 * lib/mfa-totp.ts does: on auth-js 2.105.3 the typed buckets are filtered to
 * `status === 'verified'` while `all` carries every factor, and a shape that
 * ever changes its mind about that must not silently return nothing. Deduped by
 * id, then filtered to verified — an UNVERIFIED webauthn factor is a half-
 * finished enrolment and must never be offered as a way to sign in.
 */
export async function listPasskeyFactors(): Promise<PasskeyFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  return passkeyFactorsFrom(data);
}

/** The reconciliation itself, PURE, so a caller that already holds a
 *  `listFactors()` response (the Security Centre does) does not pay for a
 *  second network round trip just to read the same list. */
export function passkeyFactorsFrom(data: unknown): PasskeyFactor[] {
  const byId = new Map<string, { id: string; status?: string; friendly_name?: string; created_at?: string }>();
  const all = (data as { all?: unknown[] } | null)?.all ?? [];
  for (const raw of all) {
    const f = raw as { id?: string; factor_type?: string; status?: string; friendly_name?: string; created_at?: string };
    if (f.id && f.factor_type === 'webauthn') byId.set(f.id, { id: f.id, ...f });
  }
  const typed = (data as { webauthn?: unknown[] } | null)?.webauthn ?? [];
  for (const raw of typed) {
    const f = raw as { id?: string; status?: string; friendly_name?: string; created_at?: string };
    if (f.id && !byId.has(f.id)) byId.set(f.id, { id: f.id, ...f });
  }
  return [...byId.values()]
    .filter((f) => f.status === 'verified')
    .map((f) => ({
      id: f.id,
      friendlyName: f.friendly_name,
      createdAt: f.created_at ?? new Date(0).toISOString(),
    }));
}

/** Why a passkey ceremony did not complete. Only `failed` is worth an error
 *  colour — a cancel is a user changing their mind, and `unsupported` is an
 *  environment fact. */
export type PasskeyOutcome =
  | { status: 'ok' }
  | { status: 'cancelled' }
  | { status: 'unsupported'; message: string }
  | { status: 'failed'; message: string };

/** A cancelled ceremony throws `NotAllowedError` / `AbortError` in every
 *  browser. Treated as a non-event, never an error banner. */
function isCancellation(message: string): boolean {
  return /notallowed|abort|cancel|timed? ?out/i.test(message);
}

/** A project without WebAuthn factors enabled answers with one of these rather
 *  than a factor. Reported as "not available here", never as a failure the user
 *  could act on. */
function isFactorUnavailable(message: string): boolean {
  return /not enabled|unsupported|disabled|not_?found|422|404|unavailable/i.test(message);
}

/**
 * Enrol a passkey as a Supabase factor.
 *
 * `mfa.webauthn.register()` performs enrol + challenge + verify in one call and
 * returns a fresh session, so a successful enrolment leaves the user at aal2 —
 * the same place a completed TOTP enrolment leaves them. There is no
 * scan-then-verify gap here and therefore none of the silent-secret-
 * regeneration hazard the TOTP path has to guard against (lib/mfa-totp.ts):
 * a passkey has no secret to display, so there is no already-scanned QR to
 * invalidate. Re-running it simply adds another credential.
 */
export async function enrollPasskeyFactor(friendlyName: string): Promise<PasskeyOutcome> {
  const api = webauthnApi();
  if (!api) {
    return {
      status: 'unsupported',
      message: 'Passkeys aren’t available in this browser.',
    };
  }
  try {
    const { error } = await api.register({ friendlyName });
    if (error) {
      const message = error.message ?? 'Passkey setup failed.';
      if (isCancellation(message)) return { status: 'cancelled' };
      if (isFactorUnavailable(message)) {
        return {
          status: 'unsupported',
          message: 'Passkey sign-in isn’t switched on for this environment yet.',
        };
      }
      return { status: 'failed', message };
    }
    return { status: 'ok' };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Passkey setup failed.';
    if (isCancellation(message)) return { status: 'cancelled' };
    return { status: 'failed', message };
  }
}

/**
 * Sign in with an enrolled passkey.
 *
 * On success Supabase issues a token carrying `aal: 'aal2'` and an `amr` entry
 * of `mfa/webauthn`. That is a genuine AAL promotion — identical in standing to
 * the one a TOTP code produces — so a user with a passkey needs no authenticator
 * app at all.
 */
export async function verifyWithPasskeyFactor(factorId: string): Promise<PasskeyOutcome> {
  const api = webauthnApi();
  if (!api) {
    return {
      status: 'unsupported',
      message: 'Passkeys aren’t available in this browser.',
    };
  }
  try {
    const { error } = await api.authenticate({ factorId });
    if (error) {
      const message = error.message ?? 'Passkey sign-in failed.';
      if (isCancellation(message)) return { status: 'cancelled' };
      return { status: 'failed', message };
    }
    return { status: 'ok' };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Passkey sign-in failed.';
    if (isCancellation(message)) return { status: 'cancelled' };
    return { status: 'failed', message };
  }
}

/** Remove a passkey factor. Shares `mfa.unenroll` with TOTP — a factor is a
 *  factor to Supabase once enrolled. */
export async function unenrollPasskeyFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}
